// Gridded field access: bilinear interpolation over the /api/conditions grid, per time step.
// Directions (from-bearings) are interpolated as vectors so 350° and 10° average to 0°, not 180°.
export class Field {
  constructor(data) { this.data = data; this.g = data.grid; this.f = data.fields; this.times = data.times; }
  get nSteps() { return this.times.length; }
  idx(r, c) { return r * this.g.nlon + c; }
  // fractional grid coords, clamped to the grid
  rc(lon, lat) {
    const g = this.g;
    const c = Math.min(Math.max((lon - g.lon0) / g.dlon, 0), g.nlon - 1.0001);
    const r = Math.min(Math.max((lat - g.lat0) / g.dlat, 0), g.nlat - 1.0001);
    return [r, c];
  }
  raw(key, r, c, t) { const a = this.f[key]; if (!a) return null; const v = a[this.idx(r, c)]; return v ? v[t] : null; }
  scalar(key, lon, lat, t) {
    const [r, c] = this.rc(lon, lat); const r0 = Math.floor(r), c0 = Math.floor(c), fr = r - r0, fc = c - c0;
    const v00 = this.raw(key, r0, c0, t), v01 = this.raw(key, r0, c0 + 1, t), v10 = this.raw(key, r0 + 1, c0, t), v11 = this.raw(key, r0 + 1, c0 + 1, t);
    if ([v00, v01, v10, v11].some(v => v == null || Number.isNaN(v))) return v00 ?? v01 ?? v10 ?? v11 ?? null;
    return (v00 * (1 - fc) + v01 * fc) * (1 - fr) + (v10 * (1 - fc) + v11 * fc) * fr;
  }
  // vector from a magnitude key + from-direction key → {u, v, speed, dir} in the same units as magnitude, u east+, v north+ (direction wind blows TO)
  vector(magKey, dirKey, lon, lat, t) {
    const [r, c] = this.rc(lon, lat); const r0 = Math.floor(r), c0 = Math.floor(c), fr = r - r0, fc = c - c0;
    let u = 0, v = 0, wsum = 0;
    for (const [rr, cc, w] of [[r0, c0, (1 - fr) * (1 - fc)], [r0, c0 + 1, (1 - fr) * fc], [r0 + 1, c0, fr * (1 - fc)], [r0 + 1, c0 + 1, fr * fc]]) {
      if (w === 0) continue;
      const m = this.raw(magKey, rr, cc, t), d = this.raw(dirKey, rr, cc, t);
      if (m == null || d == null) continue;
      const a = (d + 180) * Math.PI / 180; // TO-direction
      u += Math.sin(a) * m * w; v += Math.cos(a) * m * w; wsum += w;
    }
    if (!wsum) return { u: 0, v: 0, speed: 0, dir: 0 };
    u /= wsum; v /= wsum;
    const speed = Math.hypot(u, v);
    const dir = ((Math.atan2(u, v) * 180 / Math.PI) + 180 + 360) % 360; // back to FROM bearing
    return { u, v, speed, dir };
  }
  // everything the assessment needs at one point/time, in the prototype's shape
  at(lon, lat, t) {
    const w = this.vector('ws', 'wd', lon, lat, t), s = this.vector('sh', 'sd', lon, lat, t), h = this.vector('wh', 'wdir', lon, lat, t);
    return { t: this.times[t], ws: +w.speed.toFixed(1), wd: Math.round(w.dir), g: +(this.scalar('g', lon, lat, t) ?? 0).toFixed(1),
      wh: +h.speed.toFixed(2), wdir: Math.round(h.dir), wp: +(this.scalar('wp', lon, lat, t) ?? 0).toFixed(1),
      sh: +s.speed.toFixed(2), sd: Math.round(s.dir), sp: +(this.scalar('sp', lon, lat, t) ?? 0).toFixed(1),
      cc: Math.round(this.scalar('cc', lon, lat, t) ?? 0), pp: Math.round(this.scalar('pp', lon, lat, t) ?? 0), wc: Math.round(this.scalar('wc', lon, lat, t) ?? 0),
      sst: this.scalar('sst', lon, lat, t), ta: this.scalar('ta', lon, lat, t) };
  }
  // area mean of a scalar over the whole grid at step t
  mean(key, t) { const a = this.f[key]; if (!a) return null; let s = 0, n = 0; for (const p of a) { const v = p[t]; if (v != null) { s += v; n++; } } return n ? s / n : null; }
}
